import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useGlobalMessageActions } from "../../../entities/globalMessage";
import { useMyActions } from "../../../entities/my";
import { MyError } from "../../../shared/lib/error/MyError";
import { AuthError } from "../../../shared/lib/error/AuthError";
import { DropDownListSelected } from "../../../shared/ui/dropDownSelected";
import { IItem } from "../../../shared/model/types";
import classes from "./workTgSelector.module.scss";

interface IProps {
    title?: string;
    selectedItems: IItem[];
    onChange: (items: IItem[]) => void;
    getData: () => Promise<IItem[]>;
    multiple?: boolean;
    error?: string;
    setError?: (err: string) => void;
    searchPlaceholder?: string;
    reloadTrigger?: number;
    placeholder?: string;
}

export const WorkTgSelector: FC<IProps> = ({
    title = '',
    selectedItems,
    onChange,
    getData,
    multiple = false,
    error,
    setError,
    searchPlaceholder = 'Поиск по рабочим ТГ',
    reloadTrigger,
    placeholder,
}) => {
    const {setGlobalMessage} = useGlobalMessageActions()
    const {setIsAuth} = useMyActions()

    const [items, setItems] = useState<IItem[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const getDataRef = useRef(getData)

    useEffect(() => {
        getDataRef.current = getData
    }, [getData])

    const loadItems = async () => {
        try{
            setIsLoading(true)
            const itemsRes = await getDataRef.current()
            setItems(itemsRes)
        }
        catch(e){
            console.log(e)
            if(e instanceof AuthError){
                setIsAuth(false)
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else if(e instanceof MyError){
                setGlobalMessage({message: e.message, type: 'error'})
            }
            else{
                setGlobalMessage({message: 'Ошибка при получении списка рабочих ТГ', type: 'error'})
            }
        }
        finally{
            setIsLoading(false)
        }
    }

    useEffect(() => {
        loadItems()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reloadTrigger])

    const normalizedSelectedItems = useMemo(() => {
        return selectedItems.map(selectedItem =>
            items.find(item => item.id === selectedItem.id || item.name === selectedItem.name) || selectedItem
        )
    }, [items, selectedItems])


    const onSelected = (item: IItem) => {
        return (selected: boolean) => {
            let nextItems: IItem[] = []

            if(selected){
                if(multiple){
                    nextItems = [...normalizedSelectedItems.filter(selectedItem => selectedItem.id !== item.id), item]
                }
                else{
                    nextItems = [item]
                }
            }
            else{
                nextItems = normalizedSelectedItems.filter(
                    selectedItem => selectedItem.id !== item.id && selectedItem.name !== item.name
                )
            }

            onChange(nextItems)
            setError && setError('')
        }
    }

    const onSelectAll = (selectedIds: number[]) => {
        const availableItems = [...normalizedSelectedItems, ...items]
        const nextItems = selectedIds
            .map(id => availableItems.find(item => item.id === id))
            .filter((item): item is IItem => Boolean(item))

        onChange(nextItems)
        setError && setError('')
    }

    return (
        <section className={classes.container}>
            {title && <section className={classes.title}>{title}</section>}
            <DropDownListSelected
                isLoading={isLoading}
                items={items}
                selectedIdItems={normalizedSelectedItems.map(item => item.id)}
                onSelected={onSelected}
                onSelectAll={multiple ? onSelectAll : undefined}
                selectedCount={multiple}
                placeholder={placeholder || (multiple ? 'Выберите рабочие ТГ' : 'Выберите рабочий ТГ')}
                search
                searchPlaceholder={searchPlaceholder}
            />
            <section className={classes.errorText}>
                {error && <>*{error}</>}
            </section>
        </section>
    )
}
